import type { DataStore } from '../data/store';
import type { Id, IsoDate, Manifest, YearMonth } from '../data/types';

export type RouteId = 'home' | 'net-worth' | 'accounts' | 'expenses' | 'income' | 'transactions';
export type FilterMode = 'stock' | 'flow' | 'transactions';

export const ROUTES: ReadonlyArray<{ id: RouteId; path: string; label: string; icon: string }> = [
  { id: 'home', path: '/', label: 'Home', icon: 'home' },
  { id: 'net-worth', path: '/net-worth', label: 'Net worth', icon: 'trend' },
  { id: 'accounts', path: '/accounts', label: 'Account', icon: 'wallet' },
  { id: 'expenses', path: '/expenses', label: 'Spese', icon: 'arrow-down' },
  { id: 'income', path: '/income', label: 'Entrate', icon: 'arrow-up' },
  { id: 'transactions', path: '/transactions', label: 'Transazioni', icon: 'list' },
];

function routeFromPath(pathname: string): RouteId {
  return ROUTES.find((route) => route.path === pathname)?.id ?? 'home';
}

function routePath(routeId: RouteId): string {
  return ROUTES.find((route) => route.id === routeId)?.path ?? '/';
}

function routeSupportsCategories(routeId: RouteId): boolean {
  return (
    routeId === 'accounts' ||
    routeId === 'expenses' ||
    routeId === 'income' ||
    routeId === 'transactions'
  );
}

export class AppState {
  route = $state<RouteId>(routeFromPath(window.location.pathname));
  fromMonth = $state<YearMonth | null>(null);
  toMonth = $state<YearMonth | null>(null);
  fromDate = $state<IsoDate | null>(null);
  toDate = $state<IsoDate | null>(null);
  minMonth = $state<YearMonth | null>(null);
  maxMonth = $state<YearMonth | null>(null);
  minDate = $state<IsoDate | null>(null);
  maxDate = $state<IsoDate | null>(null);
  accountIds = $state<Id[]>([]);
  categoryIds = $state<Id[]>([]);
  accountFilterActive = $state(false);
  categoryFilterActive = $state(false);
  includeUncategorized = $state(false);
  filterSheetOpen = $state(false);
  dark = $state(localStorage.getItem('money-theme') === 'dark');

  get filterMode(): FilterMode {
    if (this.route === 'transactions') return 'transactions';
    if (this.route === 'expenses' || this.route === 'income') return 'flow';
    return 'stock';
  }

  get categoryFiltersAvailable(): boolean {
    return routeSupportsCategories(this.route);
  }

  get activeFilterCount(): number {
    return (
      Number(this.periodFilterActive) +
      Number(this.accountFilterActive) +
      Number(this.categoryFiltersAvailable && this.categoryFilterActive)
    );
  }

  get periodFilterActive(): boolean {
    return this.filterMode === 'stock'
      ? this.fromMonth !== this.minMonth || this.toMonth !== this.maxMonth
      : this.fromDate !== this.minDate || this.toDate !== this.maxDate;
  }

  initialize(store: DataStore, range?: Manifest['range']): void {
    const firstMonth = store.months[0];
    const lastMonth = store.months.at(-1);
    const firstDate = range?.first_date ?? store.core.calendar[0]?.date;
    const lastDate = range?.last_date ?? store.core.calendar.at(-1)?.date;
    if (!firstMonth || !lastMonth || !firstDate || !lastDate) return;

    this.minMonth = firstMonth;
    this.maxMonth = lastMonth;
    this.minDate = firstDate;
    this.maxDate = lastDate;

    const params = new URLSearchParams(window.location.search);
    this.fromMonth = (params.get('fromMonth') as YearMonth | null) ?? firstMonth;
    this.toMonth = (params.get('toMonth') as YearMonth | null) ?? lastMonth;
    this.fromDate = (params.get('from') as IsoDate | null) ?? firstDate;
    this.toDate = (params.get('to') as IsoDate | null) ?? lastDate;
    this.accountFilterActive = params.has('accounts');
    this.accountIds = params.get('accounts')?.split(',').filter(Boolean) ?? [];
    this.categoryFilterActive = routeSupportsCategories(this.route) && params.has('categories');
    this.categoryIds = this.categoryFilterActive
      ? (params.get('categories')?.split(',').filter(Boolean) ?? [])
      : [];
    this.includeUncategorized =
      routeSupportsCategories(this.route) && params.get('uncategorized') === '1';
  }

  navigate(route: RouteId, replace = false): void {
    this.route = route;
    const url = this.toUrl(route);
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
    requestAnimationFrame(() => document.querySelector<HTMLElement>('main h1')?.focus());
  }

  syncUrl(): void {
    window.history.replaceState({}, '', this.toUrl(this.route));
  }

  toUrl(route: RouteId): string {
    const params = new URLSearchParams();
    if (this.fromMonth) params.set('fromMonth', this.fromMonth);
    if (this.toMonth) params.set('toMonth', this.toMonth);
    if (this.fromDate) params.set('from', this.fromDate);
    if (this.toDate) params.set('to', this.toDate);
    if (this.accountFilterActive) params.set('accounts', this.accountIds.join(','));
    if (routeSupportsCategories(route) && this.categoryFilterActive)
      params.set('categories', this.categoryIds.join(','));
    if (routeSupportsCategories(route) && this.categoryFilterActive && this.includeUncategorized)
      params.set('uncategorized', '1');
    const query = params.toString();
    return `${routePath(route)}${query ? `?${query}` : ''}`;
  }

  toggleTheme(): void {
    this.dark = !this.dark;
    localStorage.setItem('money-theme', this.dark ? 'dark' : 'light');
    document.documentElement.dataset.theme = this.dark ? 'dark' : 'light';
    window.dispatchEvent(new CustomEvent('money-theme-change'));
  }

  resetFilters(): void {
    this.fromMonth = this.minMonth;
    this.toMonth = this.maxMonth;
    this.fromDate = this.minDate;
    this.toDate = this.maxDate;
    this.accountIds = [];
    this.categoryIds = [];
    this.accountFilterActive = false;
    this.categoryFilterActive = false;
    this.includeUncategorized = false;
    this.syncUrl();
  }
}
